/**
 * The Layer component renders its children within the host from the
 * LayerManager. With Layer, there is no need for the `z-index` css property
 * because order relies on the stacking context.
 */
import * as React from "react"
import * as ReactDOM from "react-dom"
import LayerContext from "./LayerContext"
import { nth } from "ramda"

type Props = Readonly<{
  children: React.ReactChild | React.ReactFragment | boolean | null | undefined
  /**
   * The location (child order) the layer will be inserted in the `host` element.
   */
  index?: number
  /**
   * A handler that is called when the Layer has mounted.
   */
  onMount?: () => void
  /**
   * A handler that is called when the Layer will be unmounted.
   */
  onUnmount?: () => void
}>

const Layer = ({ children, ...rest }: Props) => {
  const [{ index, onMount, onUnmount }] = React.useState(rest)
  const { host: defaultHost } = React.useContext(LayerContext)
  const host = React.useMemo(() => defaultHost, [defaultHost])
  const [container, setContainer] = React.useState<HTMLElement | null>(null)

  React.useLayoutEffect(() => {
    if (!host) return
    const doc = host.ownerDocument ?? document
    const layer = doc.createElement("div")
    const sibling =
      typeof index === "number" ? nth(index, Array.from(host.children)) : null
    sibling ? host.insertBefore(layer, sibling) : host.appendChild(layer)

    setContainer(layer)
    onMount?.()
    return () => {
      host && layer && host.contains(layer) && host.removeChild(layer)
      setContainer(null)
      onUnmount?.()
    }
  }, [host, index, onMount, onUnmount])

  return container && ReactDOM.createPortal(children, container)
}
Layer.displayName = "Layer"

export default React.memo(Layer)
